import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { TrashIcon, MinusIcon, PlusIcon, ShoppingBagIcon } from "@heroicons/react/24/outline";
import { useCart } from "../context/CartContext";
import { resolveImageUrl } from "../utils/image";

export default function CartPage() {
  const { cart, removeFromCart, updateQuantity, isLoading } = useCart();
  const navigate = useNavigate();

  if (!cart || cart.items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <ShoppingBagIcon className="h-20 w-20 text-gray-200 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Your cart is empty</h2>
        <p className="text-gray-500 mb-6">Start shopping to add items to your cart</p>
        <Link to="/products" className="btn-primary py-3 px-8">Browse Products</Link>
      </div>
    );
  }

  const shippingFree = cart.total >= 999;
  const shipping = shippingFree ? 0 : 79;
  const razorpayFee = Math.round(cart.total * 0.025 * 100) / 100;
  const grandTotal = cart.total + shipping + razorpayFee;

  return (
    <>
      <Helmet><title>Cart – Kushi Fashions</title></Helmet>
      <div className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Shopping Cart ({cart.item_count} items)</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.items.map((item) => (
              <div key={item.id} className="card p-4 flex gap-4">
                <img
                  src={resolveImageUrl(item.product_image, "https://via.placeholder.com/100x130/f9a8d4/ec4899?text=Dress")}
                  alt={item.product_name}
                  className="w-24 h-32 object-cover rounded-lg flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-800 line-clamp-2">{item.product_name}</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Size: {item.size}
                    {item.color && <span> &nbsp;·&nbsp; Color: {item.color}</span>}
                  </p>
                  <p className="text-primary-600 font-bold mt-2">
                    ₹{Number(item.price_at_add).toLocaleString("en-IN")}
                  </p>
                </div>
                <div className="flex flex-col items-end justify-between">
                  <button
                    onClick={() => removeFromCart(item.id)}
                    className="text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                  <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      disabled={isLoading}
                      className="p-1.5 hover:bg-gray-50"
                    >
                      <MinusIcon className="h-4 w-4" />
                    </button>
                    <span className="px-3 text-sm font-medium">{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      disabled={isLoading}
                      className="p-1.5 hover:bg-gray-50"
                    >
                      <PlusIcon className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="font-semibold text-gray-800">
                    ₹{item.subtotal.toLocaleString("en-IN")}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Order summary */}
          <div>
            <div className="card p-5 space-y-4 sticky top-24">
              <h2 className="font-bold text-lg text-gray-900">Order Summary</h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span>₹{cart.total.toLocaleString("en-IN")}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Shipping</span>
                  <span className={shippingFree ? "text-green-600" : ""}>
                    {shippingFree ? "FREE" : `₹${shipping}`}
                  </span>
                </div>
                {!shippingFree && (
                  <p className="text-xs text-primary-500">
                    Add ₹{(999 - cart.total).toFixed(0)} more for free shipping!
                  </p>
                )}
                <div className="flex justify-between text-gray-600">
                  <span>Online payment fee (2.5%)</span>
                  <span>₹{razorpayFee.toLocaleString("en-IN")}</span>
                </div>
                <hr className="border-gray-100" />
                <div className="flex justify-between font-bold text-base text-gray-900">
                  <span>Total</span>
                  <span>₹{grandTotal.toLocaleString("en-IN")}</span>
                </div>
              </div>
              <button
                onClick={() => navigate("/checkout")}
                className="btn-primary w-full py-3"
              >
                Proceed to Checkout
              </button>
              <Link to="/products" className="block text-center text-sm text-primary-600 hover:underline">
                Continue Shopping
              </Link>

              {/* Offline purchase notice */}
              <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800 space-y-1">
                <p className="font-semibold">💡 Buy offline & save on charges!</p>
                <p>Contact us directly to avoid the 2.5% online payment fee:</p>
                <p className="font-medium">📞 8123429542 &nbsp;|&nbsp; 7075174810</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
